import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import dayjs from 'dayjs'
import { X, CalendarDays } from '@/components/icons'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer'
import { Button } from './ui/button'
import { PrntrCard } from './ui/prntr-input'
import { DateRow, NumRow, TextRow, TextareaField } from './ui/prntr-fields'
import { useEquipmentCreate, useEquipmentUpdate } from '../lib/hooks'
import { useIsAdmin } from '@/hooks/useIsCompanyAdmin'

// A single equipment item, as returned by useEquipmentGetByAircraft.
export type EquipmentItem = {
  id: string
  name: string
  type: string | null
  serialNumber: string | null
  location: string | null
  quantity: number
  expiryDate: string | Date | null
  notes: string | null
}

interface AircraftEquipmentFormDrawerProps {
  aircraftId: string
  open: boolean
  onClose: () => void
  // When set, the drawer edits this item; otherwise it creates a new one.
  item?: EquipmentItem | null
}

const NOTES_MAX_LENGTH = 500

const EquipmentFormSchema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
  type: z.string().nullable().optional(),
  serialNumber: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
  quantity: z
    .number({ invalid_type_error: 'Enter a quantity' })
    .int()
    .min(1, 'At least 1'),
  expiryDate: z.string().nullable().optional(),
  notes: z.string().max(NOTES_MAX_LENGTH, 'Notes are too long').optional(),
})

type EquipmentFormData = z.infer<typeof EquipmentFormSchema>

const EMPTY_FORM: EquipmentFormData = {
  name: '',
  type: null,
  serialNumber: null,
  location: null,
  quantity: 1,
  expiryDate: null,
  notes: '',
}

function emptyToNull(value: string | null | undefined): string | null {
  return value?.trim() ? value.trim() : null
}

export function AircraftEquipmentFormDrawer({
  aircraftId,
  open,
  onClose,
  item,
}: AircraftEquipmentFormDrawerProps) {
  const { isAdmin } = useIsAdmin()
  const { mutate: createEquipment, isPending: isCreating } = useEquipmentCreate(aircraftId)
  const { mutate: updateEquipment, isPending: isUpdating } = useEquipmentUpdate(aircraftId)

  const editingId = item?.id ?? null
  const isSaving = isCreating || isUpdating

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm<EquipmentFormData>({
    resolver: zodResolver(EquipmentFormSchema),
    defaultValues: EMPTY_FORM,
    mode: 'onChange',
  })

  // Sync the form to the target item (edit) or fresh defaults (create) when
  // the drawer opens.
  useEffect(() => {
    if (!open) return
    if (item) {
      reset({
        name: item.name,
        type: item.type,
        serialNumber: item.serialNumber,
        location: item.location,
        quantity: item.quantity,
        expiryDate: item.expiryDate ? dayjs.utc(item.expiryDate).format('YYYY-MM-DD') : null,
        notes: item.notes ?? '',
      })
    } else {
      reset(EMPTY_FORM)
    }
    void trigger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item])

  const notesLength = watch('notes')?.length ?? 0

  const onSubmit = handleSubmit((data) => {
    const fields = {
      name: data.name.trim(),
      type: emptyToNull(data.type),
      serialNumber: emptyToNull(data.serialNumber),
      location: emptyToNull(data.location),
      quantity: data.quantity,
      // Stored as UTC midnight so the daily expiry scan matches the picked day.
      expiryDate: data.expiryDate ? dayjs.utc(data.expiryDate).toDate() : null,
      notes: emptyToNull(data.notes),
    }

    if (editingId) {
      updateEquipment(
        { id: editingId, ...fields },
        {
          onSuccess: () => {
            toast.success('Equipment updated')
            onClose()
          },
          onError: () => toast.error('Failed to update equipment'),
        }
      )
      return
    }

    createEquipment(
      { aircraftId, ...fields },
      {
        onSuccess: () => {
          toast.success('Equipment added')
          onClose()
        },
        onError: () => toast.error('Failed to add equipment'),
      }
    )
  })

  return (
    <Drawer open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DrawerContent className="bg-surface-L0 flex flex-col max-h-[85dvh]">
        <DrawerHeader className="flex flex-row items-center justify-between p-4 shrink-0">
          <DrawerTitle className="text-lg font-semibold text-content-primary-bold">
            {!isAdmin ? 'Equipment' : editingId ? 'Edit Equipment' : 'Add Equipment'}
          </DrawerTitle>
          <Button
            variant="secondary"
            size="icon"
            roundness="round"
            className="size-8"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </Button>
        </DrawerHeader>

        <div
          className="min-h-0 flex-1 overflow-y-auto px-4 pt-4 pb-6"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <form
            id="equipment-form"
            onSubmit={onSubmit}
            className="flex flex-col gap-4"
          >
            <PrntrCard>
              <TextRow
                control={control}
                name="name"
                label="Name"
                placeholder="e.g. Fire extinguisher"
                nullable={false}
                readOnly={!isAdmin}
                error={errors.name?.message}
              />
              <TextRow
                control={control}
                name="type"
                label="Type"
                placeholder="e.g. Halon 1211"
                readOnly={!isAdmin}
              />
              <TextRow
                control={control}
                name="serialNumber"
                label="Serial Number"
                autoCapitalize="characters"
                readOnly={!isAdmin}
              />
              <TextRow
                control={control}
                name="location"
                label="Location"
                placeholder="e.g. Cabin forward"
                readOnly={!isAdmin}
              />
              <NumRow
                control={control}
                name="quantity"
                label="Quantity"
                integer
                readOnly={!isAdmin}
                error={errors.quantity?.message}
              />
              <DateRow
                control={control}
                name="expiryDate"
                label={
                  <span className="flex items-center gap-2">
                    <CalendarDays className="size-4" /> Expiry Date
                  </span>
                }
                readOnly={!isAdmin}
              />
            </PrntrCard>
            <div className="flex flex-col gap-1">
              <TextareaField
                control={control}
                name="notes"
                label="Notes"
                placeholder="e.g. Next hydrostatic test due with the C-check"
                readOnly={!isAdmin}
                error={errors.notes?.message}
              />
              {isAdmin && (
                <p className="text-xs text-content-primary-subtle">
                  {notesLength}/{NOTES_MAX_LENGTH} characters
                </p>
              )}
            </div>
          </form>
        </div>

        <div className="shrink-0 border-t border-border-L2 px-4 pt-3 pb-4">
          {isAdmin ? (
            <div className="flex flex-row gap-2">
              <Button
                type="button"
                variant="secondary"
                size="mini"
                className="flex-1"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="equipment-form"
                variant="primary"
                size="mini"
                className="flex-1"
                disabled={isSaving || !isValid || (!!editingId && !isDirty)}
                loading={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="secondary"
              size="mini"
              className="w-full"
              onClick={onClose}
            >
              Close
            </Button>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  )
}
